import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { deletePath, getPathData } from "../../api/ProjectAPI";
import {
  useCustomAlertStore,
  useDownloadModalStore,
  usePathStore,
  usePointStore,
} from "../../Store/store";
import {
  PotreeGenerateAreaStore,
  useHeadlandWorkAreaStore,
} from "../../Store/PotreeStore";
import {
  cleanupAllObstacleObjects,
  loadExistingPath,
} from "./PathRemove_Create";
import { DownloadIcon } from "../../assets/icons/WorkTypeIcons";
import { vehicleSpecs } from "../../Store/store";
import { generateUniformWaypoints } from "./generateUniformWaypoints";

const PathSelectCardContainer = styled.div`
  cursor: pointer;
  background-color: #0404045e;
  border-radius: 10px;
  padding: 20px 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  main {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .path_item_header_section {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .path_item_date_section {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .path_item_title {
    font-size: 15px;
    font-weight: 400;
    color: #fff;
  }

  .path_item_description {
    font-size: 13px;
    color: #a6a6a6;
  }

  .path_item_delete_button {
    background-color: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 0;
    margin: 0;
    width: 16px;
    height: 16px;
  }

  .AreaSelectItemDownloadButton {
    background-color: rgba(0, 255, 153, 0.1);
    border: 1px solid #00ff99;
    cursor: pointer;
    color: #00ff99;
    font-size: 12px;
    border-radius: 8px;
    padding: 5px 10px;
    display: flex;
    align-items: center;
    gap: 5px;
    max-width: 100px;
    justify-content: center;
    align-self: flex-end;
  }
`;

const ProfileImage = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #853030;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
`;

export default function PathSelectCard({ path, fetchPathList }) {
  const { projectId } = useParams();
  const { showWarningAlert } = useCustomAlertStore();
  const { openModal } = useDownloadModalStore();
  const { safetyMargin } = PotreeGenerateAreaStore();
  const {
    viewer,
    setMarkers,
    setNodes,
    setEdges,
    edges,
    setRibbonMeshs,
    setPolyline,
    setRibbonMesh,
    setPathGenerateMode,
    setPathEditMode,
    setAllObstaclePoints,
    setClosedObstacleArea,
    closedObstacleArea,
    vehicleSpec,
    setPathData,
    waypoints,
    setWaypoints,
    pathData,
    selectedEquipmentId,
    pathGenerateMode,
    setEquipmentWaypoints,
  } = usePathStore();

  const fetchPath = async (waypointId) => {
    cleanupAllObstacleObjects(viewer);

    setAllObstaclePoints([]);
    setClosedObstacleArea([]);

    const response = await getPathData(projectId, waypointId);

    setPathData(response);
    loadExistingPath(
      response,
      viewer,
      setMarkers,
      setNodes,
      setEdges,
      setRibbonMeshs,
      setPolyline,
      setRibbonMesh,
      setPathGenerateMode,
      setPathEditMode,
      setAllObstaclePoints,
      setClosedObstacleArea,
      vehicleSpec,
      closedObstacleArea,
      selectedEquipmentId,
      pathGenerateMode,
      setWaypoints,
      setEquipmentWaypoints,
    );
  };

  const date = path.createdAt.split(" ")[0].split("-").join(".");

  const fetchDelzetePath = async (id) => {
    showWarningAlert("경로 삭제", "경로를 삭제하시겠습니까?", async () => {
      const response = await deletePath(id);
      fetchPathList();
    });
  };

  const handleDownloadClick = async (e, id) => {
    e.stopPropagation();
    const response = await getPathData(projectId, id);

    // 장애물 데이터 추출
    const obstacles = response.obstacles;

    // 차량 스펙 추출
    const agriculturalEquipmentId = response.agriculturalEquipmentId;
    const vehicleSpec = vehicleSpecs[agriculturalEquipmentId];

    // 경로 데이터 추출
    await fetchPath(id);

    // 헤드랜드 및 작업 영역 데이터 가져오기 (현재 화면에서 생성된 경로인 경우)
    const { headlandData, workAreaPolygon } =
      useHeadlandWorkAreaStore.getState();

    // 최종 data 객체로 통합
    const data = {
      // 서버 원본 메타데이터
      projectId,
      id: response.id,
      name: response.name,
      description: response.description,
      workType: response.workType,
      createdAt: response.createdAt,
      // createdByName: response.createdByName,

      // 차량 스펙
      agriculturalEquipmentId,
      agriculturalEquipmentName: response.agriculturalEquipmentName,
      equipmentSpec: vehicleSpec,

      // 경로 정보
      waypointDetails: waypoints,

      // 장애물 정보
      obstacles,

      // 검증용 제약 조건 정보
      safetyMargin: safetyMargin, // 안전 마진 (m)
      // 헤드랜드 및 작업 영역 데이터 추가
      headlandData: headlandData, // { headland: Array<[x,y]>, work_area: Array<[x,y]>, turning: Array<[x,y]> }
      workAreaPolygon: workAreaPolygon, // Array<[x,y]> - 작업 영역 폴리곤 좌표
    };

    //     // 원본 데이터만 모달에 전달
    waypoints && openModal(data);
  };

  return (
    <PathSelectCardContainer
      onClick={(e) => {
        e.stopPropagation();
        fetchPath(path.id);
      }}
    >
      <header>
        {/* <section className="path_item_header_section">
          <ProfileImage className="profile_image">
            {path.createdByName.charAt(0)}
          </ProfileImage>
          <h2>{path.createdByName}</h2>
        </section> */}
        <section className="path_item_date_section">
          <span className="path_item_date">{date}</span>
          <button
            className="path_item_delete_button"
            arial-lebel="경로 삭제"
            title="이 경로를 삭제합니다."
            onClick={(e) => {
              e.stopPropagation();
              fetchDelzetePath(path.id);
            }}
          >
            <svg
              aria-hidden="true" // 아이콘 자체는 스크린 리더에서 숨김
              width="16"
              height="16"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-x-lg"
                viewBox="0 0 16 16"
              >
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
              </svg>
            </svg>
          </button>
        </section>
      </header>
      <main>
        <h3 className="path_item_title">{path.name}</h3>
        <p className="path_item_description">{path.description}</p>
        <button
          aria-label="다운로드"
          className="AreaSelectItemDownloadButton"
          onClick={(e) => handleDownloadClick(e, path.id)}
        >
          <DownloadIcon size={20} color="#00ff99" /> 다운로드
        </button>
      </main>
    </PathSelectCardContainer>
  );
}
